import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { apiPost } from "@lyder-talk/core/lib/api-client";

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  email?: string;
}

export const useUserProfile = (userId: string | null) => {
  return useQuery({
    queryKey: queryKeys.userProfile.byId(userId),
    queryFn: async (): Promise<UserProfile | null> => {
      if (!userId) return null;

      try {
        // Fetch email and avatar using batch function
        const userData = (await apiPost<{ users: Array<{ user_id: string; email: string; avatar_url: string | null; display_name: string | null }> }>(
          "/v1/users/batch",
          { ids: [userId] },
        )).users;
        const error = null;

        if (error) throw error;

        let email: string | undefined;
        let avatarUrl: string | null = null;
        let displayName: string | null = null;

        if (userData && Array.isArray(userData) && userData.length > 0) {
          const user = userData[0];
          email = user.email && user.email !== "Unknown" ? user.email : undefined;
          avatarUrl = user.avatar_url;
          displayName = user.display_name || null;
        }

        // Fallback: try to get email from current session if it's the current user
        if (!email) {
          try {
            const { data: { session } } = await auth.getSession();
            if (session?.user?.id === userId) {
              email = session.user.email || undefined;
            }
          } catch (sessionErr) {
            // Ignore session errors
          }
        }

        return {
          id: userId,
          display_name: displayName,
          avatar_url: avatarUrl,
          email,
        };
      } catch (err) {
        console.error(`Error fetching user profile for user ${userId}:`, err);
        return {
          id: userId,
          display_name: null,
          avatar_url: null,
          email: undefined,
        };
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const useUserProfiles = (userIds: string[]) => {
  return useQuery({
    queryKey: queryKeys.userProfiles.byIds(userIds.sort().join(",")),
    queryFn: async (): Promise<Map<string, UserProfile>> => {
      if (userIds.length === 0) return new Map();

      const uniqueIds = [...new Set(userIds)].filter(Boolean);
      if (uniqueIds.length === 0) return new Map();

      const profileMap = new Map<string, UserProfile>();
      
      try {
        // Fetch all user data in a single batch call
        const userData = (await apiPost<{ users: Array<{ user_id: string; email: string; avatar_url: string | null; display_name: string | null }> }>(
          "/v1/users/batch",
          { ids: uniqueIds },
        )).users;
        const error = null;

        if (error) {
          console.error("Error fetching batch user data:", error);
          throw error;
        }

        if (userData && Array.isArray(userData)) {
          // Build the profile map from batch results
          userData.forEach((item) => {
            const email = item.email && item.email !== "Unknown" 
              ? item.email 
              : undefined;

            profileMap.set(item.user_id, {
              id: item.user_id,
              display_name: item.display_name,
              avatar_url: item.avatar_url,
              email,
            });
          });
        }

        // Fallback: try to get email from current session for any missing profiles
        for (const userId of uniqueIds) {
          if (!profileMap.has(userId) || !profileMap.get(userId)?.email) {
            try {
              const { data: { session } } = await auth.getSession();
              if (session?.user?.id === userId) {
                const existing = profileMap.get(userId);
                profileMap.set(userId, {
                  id: userId,
                  display_name: null,
                  avatar_url: existing?.avatar_url || null,
                  email: session.user.email || undefined,
                });
              }
            } catch (sessionErr) {
              // Ignore session errors
            }
          }
        }

        // Ensure all requested user IDs have entries
        for (const userId of uniqueIds) {
          if (!profileMap.has(userId)) {
            profileMap.set(userId, {
              id: userId,
              display_name: null,
              avatar_url: null,
              email: undefined,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching user profiles:", err);
        // Create fallback entries for all users
        for (const userId of uniqueIds) {
          profileMap.set(userId, {
            id: userId,
            display_name: null,
            avatar_url: null,
            email: undefined,
          });
        }
      }

      return profileMap;
    },
    enabled: userIds.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

